import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { TaskUtility } from '../task.utility';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { Subscription } from 'rxjs';
import { IntlService } from '@progress/kendo-angular-intl';
import { State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { TaskListService } from 'src/app/core/service/grid-data/grid-data-task.service';

@Component({
  standalone: false,
  selector: 'app-task-list-detail',
  templateUrl: './task-list-detail.component.html',
  styleUrls: ['./task-list-detail.component.scss']
})
export class TaskListDetailComponent implements OnInit, OnDestroy
{
  @Input() public caseId = 0;
  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public currentMemberId: number;
  public pageSizes = [5, 10, 25, 50, 100];

  /**
   * The select task from the tasks table.
   */
  public selectedTask: any = null;
  public taskOpened = false;
  public currentUser: any;
  public currentUserId: number;
  public task = null;
  public priorityDialogOpened = false;
  public taskDueDateOpened = false;
  public taskDialogOpened = false;
  public assigneeDialogOpened = false;
  public assigneeId: number;
  public taskEnableAdvanced = false;
  public taskFilters: Array<{ text: string; value: string }>;
  public selectedTaskFilter = 'OPEN';
  public taskGridView: GridDataResult;
  public taskGridData: any = {};
  public totalTasks: number;
  public windowState = 'default';
  public addedFollowUpTask = false;

  /**
   * Case list Kendo grid state.
   */
  public taskState: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [{ field: 'openedOn', dir: 'desc' }],
    take: 10
  };

  private addTaskComponentSubscription: Subscription;

  constructor(
    private taskListService: TaskListService,
    private taskUtilityService: TaskUtility,
    private eventListService: EventListService,
    private authenticationService: AuthenticationService,
    private messengerService: MessengerService,
    private appConfigService: AppConfigService,
    public intl: IntlService
  )
  {
    this.taskState.take = this.appConfigService.getConfig().pageSize;
    this.taskFilters = this.taskUtilityService.taskFilters;

    // subscribe to open new message dialog within the component
    this.addTaskComponentSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'openComponentAddTaskDialog')
        {
          this.initiateTask(message.data !== undefined ? message.data : null);
        }
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get tasksloading(): boolean
  {
    return this.taskListService.loading;
  }

  @Input() public set memberId(memberId: number)
  {
    if (memberId !== undefined)
    {
      this.currentMemberId = memberId;
      this.taskState.skip = 0;
      this.loadTasks();
    }
  }

  public ngOnInit(): void
  {
    const user = this.authenticationService.currentUser;
    this.currentUser = user;
    this.currentUserId = user ? user.id : 0;
    this.taskFilterSelected(this.selectedTaskFilter, true);
  }

  public ngOnDestroy(): void
  {
    if (this.addTaskComponentSubscription !== undefined) { this.addTaskComponentSubscription.unsubscribe(); }
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any): void
  {
    this.selectedTask = task;
    this.selectedTask.name = TextProcessor.truncateText(this.selectedTask.name, 150);
    this.selectedTask.inMemberDetail = true;
    this.addedFollowUpTask = false;
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Event handler for task completion.
   *
   * @param taskId Task ID
   */
  public onTaskCompleted(): void
  {
    this.refresh.emit();
    this.closeTaskDialog();
    this.loadTasks(true);
  }

  public followUpTaskAdded(): void 
  {
    this.addedFollowUpTask = true;
  }

  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    document.body.style.overflow = '';
    // If follow-up task was added, we must emit a refresh
    if (this.addedFollowUpTask)
    {
      this.loadTasks(true);
    }
  }

  public isChannelAccessAllowed(dataItem: any): boolean
  {
    if (dataItem && dataItem.users && dataItem.users.length > 0)
    {
      return dataItem.users.indexOf(this.currentUserId) >= 0;
    }
    else
    {
      if (dataItem.assignee && this.currentUser.userName === dataItem.assignee)
      {
        return true;
      }
      else
      {
        return false;
      }
    }
  }

  /**
   * Sets the selected task, and opens the task priority dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskPriorityDialog(task): void
  {
    this.task = task;
    this.priorityDialogOpened = true;
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
    this.messengerService.sendMessage(new MessageSubject('timelineVisibilityChanged'));
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.assigneeDialogOpened = true;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDuedateDialog(task): void
  {
    this.task = task;
    this.taskDueDateOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskAssignee(assignee): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.messengerService.sendMessage(new MessageSubject('timelineVisibilityChanged'));
    this.closeTaskAssigneeDialog();
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.messengerService.sendMessage(new MessageSubject('timelineVisibilityChanged'));
    this.taskDueDateOpened = false;
  }

  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Open the task dialog and build the task form.
   */
  public initiateTask(assigneeId: number): void
  {
    this.assigneeId = assigneeId;
    this.taskDialogOpened = true;
  }

  /**
   * Method called after a new task has been added.
   */
  public afterTaskAdded(): void
  {
    this.taskDialogOpened = false;
    this.loadTasks(true);
    this.refresh.emit();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public taskDataStateChange(state: DataStateChangeEvent): void
  {
    this.taskState = state;
    this.loadTasks();
  }

  /**
   * Task filter has been selected.
   *
   * @param filter Filter: Open, Completed, All
   */
  public taskFilterSelected(filter: string, isOnInit = false): void
  {
    this.selectedTaskFilter = filter;
    const filterChanged: any = {};
    filterChanged.filter = filter;
    if (!isOnInit)
    {
      this.loadTasks();
    }
  }

  private loadTasks(refreshPatientCount: boolean = false): void
  {
    this.taskState.filter =
    {
      logic: 'and',
      filters: []
    };

    this.taskState.filter.filters.push({ field: 'memberId', operator: 'eq', value: this.currentMemberId, logic: 'or' });
    this.taskState.filter.filters.push({ field: 'memberIdFromVar', operator: 'eq', value: this.currentMemberId, logic: 'or' });

    if (this.caseId && this.caseId > 0)
    {
      this.taskState.filter.filters.push({ field: 'id', operator: 'eq', value: this.caseId, logic: 'and' });
    }

    switch (this.selectedTaskFilter)
    {
      case 'OPEN':
        this.taskState.filter.filters.push({ field: 'closedOn', operator: 'isnull', value: '', logic: 'and' });
        break;
      case 'COMPLETED':
        this.taskState.filter.filters.push({ field: 'closedOn', operator: 'isnotnull', value: '', logic: 'and' });
        break;
    }

    this.taskListService.queryByState(this.taskState, (value: any) =>
    {
      // Preprocessing data before it's passed to the Kendo Grid.
      if (value !== null)
      {
        value.data.forEach(d =>
        {
          d.openedOn = d.openedOn !== null ? new Date(d.openedOn) : null;
          d.closedOn = d.closedOn !== null ? new Date(d.closedOn) : null;
          d.dueDate = d.dueDate !== null ? new Date(d.dueDate) : null;
          d.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(d.priority);
          d.priority = this.taskUtilityService.priority(d.priority);
          d.eventTypeIcon = this.eventListService.getEventTypeIcon(d.eventType);
          d.eventTypeText = this.eventListService.getEventTypeText(d.eventType);
          d.encounterTypeText = this.eventListService.getEncounterTypeText(d.encounterType);
        });
      }
      this.taskGridView = {
        data: (value) ? this.taskGridData = value.data : this.taskGridData = [],
        total: (value) ? this.totalTasks = value.total : this.totalTasks = 0
      };

      if (refreshPatientCount)
      {
        this.messengerService.sendMessage(new MessageSubject('refreshSummaryCounts'));
      }
    });
  }
}
